'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { INVESTOR_COUNT_CLAIM } from '@/lib/approx';

/**
 * The evidence section: why matched outreach is the right shape for the
 * problem, cited to named outside research, plus what is measurably inside
 * SendVC's own database.
 *
 * Three AI-SEO audit findings land here, and all three are the same underlying
 * gap — the homepage asserted things and cited nothing:
 *
 *   B7  no attributed quote or sourced claim anywhere on the page
 *   E12 no outbound links co-occurring with named category sources
 *   D10 data points present, but no original-research framing behind them
 *
 * CITATION RULES for anything added here, because this is the one part of the
 * site that points outward and the claims policy in .agents/product-marketing.md
 * applies with full force:
 *
 *   1. Every external figure must come from a primary source that was actually
 *      loaded, and the link must go to that source — not to a blog summarising
 *      it. Both links below were fetched and their numbers read off the
 *      original text on 2026-08-21.
 *   2. Quote briefly and attribute in visible copy, not just in the href.
 *   3. Never cite a source that flatters the product without saying what it
 *      actually measured. The Gompers et al. finding is quoted here precisely
 *      because it is inconvenient: cold inbound is the SMALLEST channel. That
 *      is the honest case for matching over volume, and it is the case the
 *      product actually makes.
 *
 * SOURCES
 *   Gompers, Gornall, Kaplan & Strebulaev, "How Do Venture Capitalists Make
 *   Decisions?", NBER Working Paper 22587 (September 2016); published in the
 *   Journal of Financial Economics 135(1), 2020, 169-190. Survey of 885
 *   institutional VCs at 681 firms. Verbatim from p.5 of the working paper:
 *   "Over 30% of deals are generated through professional networks. Another 20%
 *   are referred by other investors while 8% are referred by existing portfolio
 *   companies. Almost 30% are proactively self-generated. Only 10% come inbound
 *   from company management." Same page: "The average firm in our sample
 *   screens 200 companies and makes only four investments in a given year."
 *
 *   DocSend (Dropbox), pitch deck research: "investors spend less than 3
 *   minutes reviewing pitch decks these days".
 *
 * OWN-DATA RULES — the figures in `coverage` below are NOT marketing numbers.
 * Each one is copied from the measurement block at the top of
 * lib/investor-stats.ts, which records them as measured against production on
 * 2026-08-18 over 5,087 active rows (firm count corroborated by
 * lib/vc-discovery.ts). The date ships in the visible copy on purpose: a dated
 * measurement is a claim that can be checked, an undated one is a boast.
 *
 * check_size is shown at its real 8.7% coverage and explicitly marked as too
 * thin to segment on. Reporting a median check size off 8.7% coverage is the
 * exact failure mode the claims policy forbids — a number that looks measured
 * and is not. Publishing the weak denominator is the point of the section.
 */

/**
 * Field coverage across the active investor table. Values are data, not copy,
 * so they live here rather than in the locale files where a translator could
 * silently edit a number. Labels are translated; figures are not.
 */
const coverage: Array<{ key: string; value: string }> = [
  // Rounded DOWN, never up — owner decision 2026-08-25: no exact investor
  // counts on public pages, because the precise size of the database is
  // competitive information. The coverage PERCENTAGES below are unchanged:
  // they are the point of this section and they disclose nothing about scale.
  // See lib/approx.ts for the same rule applied to the computed pages.
  //
  // The "Distinct firms" row used to sit second, at 4,100+. It is gone site-wide
  // (owner decision, same date): the database is ONE figure, 5,000+ investors,
  // from lib/approx.ts so the claim cannot drift page by page. An "Unlimited"
  // web-discovery row briefly replaced it and was worse — plans reach 10/50/150
  // investors a month (PRICING_TIERS, types/index.ts), so a bare "Unlimited"
  // next to the headline asserts something the product does not do. The
  // uncapped-discovery point belongs in prose that can carry its qualifier.
  { key: 'contacts', value: INVESTOR_COUNT_CLAIM },
  { key: 'focusAreas', value: '94.4%' },
  { key: 'location', value: '93.1%' },
  { key: 'portfolio', value: '45.7%' },
  { key: 'checkSize', value: '8.7%' },
];

const VC_DECISIONS_URL = 'https://www.nber.org/papers/w22587';
const DOCSEND_URL = 'https://www.dropbox.com/resources/docsend-pitch-deck-research';

const Evidence: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useIntersectionObserver();

  return (
    <section id="evidence" className="py-16 md:py-24 bg-gray-50">
      <div className="container">
        <div ref={ref} className={`max-w-3xl mx-auto fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <div className="text-center mb-10">
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.home.evidence.tagline')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {t('pageContent.home.evidence.title')}
            </h2>
          </div>

          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            {t('pageContent.home.evidence.intro')}
          </p>

          <figure className="my-8 border-l-4 border-primary-400 pl-6">
            <blockquote className="text-xl text-gray-900 font-medium leading-relaxed" cite={VC_DECISIONS_URL}>
              &ldquo;{t('pageContent.home.evidence.quote')}&rdquo;
            </blockquote>
            <figcaption className="mt-3 text-sm text-gray-500">
              {t('pageContent.home.evidence.quoteAttribution')}{' '}
              <a
                href={VC_DECISIONS_URL}
                target="_blank"
                rel="noopener"
                className="text-primary-600 underline hover:text-primary-700"
              >
                {t('pageContent.home.evidence.quoteSource')}
              </a>
            </figcaption>
          </figure>

          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            {t('pageContent.home.evidence.implication')}
          </p>

          <p className="text-lg text-gray-600 leading-relaxed mb-10">
            {t('pageContent.home.evidence.relevance')}{' '}
            <a
              href={DOCSEND_URL}
              target="_blank"
              rel="noopener"
              className="text-primary-600 underline hover:text-primary-700"
            >
              {t('pageContent.home.evidence.docsendSource')}
            </a>
            .
          </p>

          {/* Original data — the part no one else can publish. */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t('pageContent.home.evidence.data.title')}
            </h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              {t('pageContent.home.evidence.data.intro')}
            </p>

            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {coverage.map(({ key, value }) => (
                <div key={key} className="rounded-lg bg-gray-50 p-4">
                  <dt className="text-sm text-gray-500 leading-snug">
                    {t(`pageContent.home.evidence.data.fields.${key}`)}
                  </dt>
                  <dd className="text-2xl font-bold text-primary-600 mt-1">{value}</dd>
                </div>
              ))}
            </dl>

            <h4 className="text-base font-semibold text-gray-900 mb-2">
              {t('pageContent.home.evidence.data.methodTitle')}
            </h4>
            <p className="text-gray-600 leading-relaxed mb-3">
              {t('pageContent.home.evidence.data.method')}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {t('pageContent.home.evidence.data.limits')}
            </p>

            <p className="mt-6 text-sm text-gray-500">
              {t('pageContent.home.evidence.data.measured')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Evidence };
